import React, { useState } from 'react';
import { Plus, Trash2, Save, X, Users } from 'lucide-react';
import { addOpcion, deleteOpcion } from '../../api/cafeteria';

const EMPTY = {
  numero_opcion: 1,
  principal: '', proteina: '', acompanamiento1: '', acompanamiento2: '',
  ensalada: '', bebida: '', postre: '', precio: '', cupos_maximos: ''
};

const CAMPOS = [
  { key: 'principal',       label: '🍚 Plato Principal',  ph: 'Ej: Arroz blanco',      required: true },
  { key: 'proteina',        label: '🍗 Proteína',         ph: 'Ej: Pollo guisado' },
  { key: 'acompanamiento1', label: '🫘 Acompañamiento 1', ph: 'Ej: Habichuelas rojas' },
  { key: 'acompanamiento2', label: '🥔 Acompañamiento 2', ph: 'Ej: Tostones' },
  { key: 'ensalada',        label: '🥬 Ensalada',         ph: 'Ej: Ensalada verde' },
  { key: 'bebida',          label: '🥤 Bebida',           ph: 'Ej: Jugo de chinola' },
  { key: 'postre',          label: '🍰 Postre',           ph: 'Ej: Flan de coco' },
];

export default function OpcionesManager({ menu, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const opciones = menu.opciones || [];
  const editable = menu.estado !== 'closed';

  // Auto-assign next numero_opcion
  const nextNumero = () => {
    const nums = opciones.map(o => o.numero_opcion || 1);
    return nums.length > 0 ? Math.max(...nums) + 1 : 1;
  };

  const openAdd = () => {
    setForm({ ...EMPTY, numero_opcion: nextNumero() });
    setShowAdd(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.principal.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await addOpcion(menu.id, {
        numero_opcion:   form.numero_opcion,
        principal:       form.principal       || null,
        proteina:        form.proteina        || null,
        acompanamiento1: form.acompanamiento1 || null,
        acompanamiento2: form.acompanamiento2 || null,
        ensalada:        form.ensalada        || null,
        bebida:          form.bebida          || null,
        postre:          form.postre          || null,
        precio:          form.precio          ? parseFloat(form.precio) : null,
        cupos_maximos:   form.cupos_maximos   ? parseInt(form.cupos_maximos) : null,
      });
      setForm(EMPTY);
      setShowAdd(false);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al agregar opción');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta opción?')) return;
    try { await deleteOpcion(id); onRefresh(); }
    catch (e) { alert(e.response?.data?.error || 'Error al eliminar'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-700 text-sm">
          Opciones de menú
          <span className="ml-2 text-xs text-gray-400 font-normal">(cada opción = menú completo)</span>
        </h4>
        {editable && !showAdd && (
          <button onClick={openAdd}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold hover:bg-orange-200">
            <Plus className="w-3.5 h-3.5" /> Agregar opción
          </button>
        )}
      </div>

      {/* Formulario */}
      {showAdd && (
        <form onSubmit={handleAdd} className="bg-orange-50 rounded-xl p-4 space-y-3 border border-orange-200">
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-orange-700">Opción #</span>
            <input type="number" value={form.numero_opcion} min="1" max="10"
              onChange={e => setForm(f => ({ ...f, numero_opcion: parseInt(e.target.value) || 1 }))}
              className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CAMPOS.map(({ key, label, ph, required }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required && ' *'}</label>
                <input type="text" value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={ph}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">💰 Precio</label>
                <input type="number" value={form.precio} step="0.01" min="0"
                  onChange={e => setForm(f => ({ ...f, precio: e.target.value }))}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">👥 Cupos máx.</label>
                <input type="number" value={form.cupos_maximos} min="1"
                  onChange={e => setForm(f => ({ ...f, cupos_maximos: e.target.value }))}
                  placeholder="Sin límite"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving || !form.principal.trim()}
              className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" onClick={() => setShowAdd(false)}
              className="flex items-center gap-1.5 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300">
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de opciones */}
      {opciones.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4">Sin opciones — agrega al menos una antes de publicar</p>
      ) : (
        <div className="space-y-3">
          {[...opciones].sort((a, b) => (a.numero_opcion || 1) - (b.numero_opcion || 1)).map((op) => (
            <div key={op.id} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {op.numero_opcion || 1}
                  </span>
                  <span className="text-sm font-bold text-gray-700">Opción {op.numero_opcion || 1}</span>
                  {op.precio && (
                    <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full font-semibold">
                      RD${parseFloat(op.precio).toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {op.cupos_maximos ? (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Users className="w-3.5 h-3.5" /> {op.cupos_usados}/{op.cupos_maximos}
                    </span>
                  ) : op.cupos_usados > 0 ? (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Users className="w-3.5 h-3.5" /> {op.cupos_usados}
                    </span>
                  ) : null}
                  {editable && (
                    <button onClick={() => handleDelete(op.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 text-sm ml-9">
                {op.principal       && <div><span className="text-[10px] text-gray-400 block">🍚 Principal</span>{op.principal}</div>}
                {op.proteina        && <div><span className="text-[10px] text-gray-400 block">🍗 Proteína</span>{op.proteina}</div>}
                {op.acompanamiento1 && <div><span className="text-[10px] text-gray-400 block">🫘 Acomp. 1</span>{op.acompanamiento1}</div>}
                {op.acompanamiento2 && <div><span className="text-[10px] text-gray-400 block">🥔 Acomp. 2</span>{op.acompanamiento2}</div>}
                {op.ensalada        && <div><span className="text-[10px] text-gray-400 block">🥬 Ensalada</span>{op.ensalada}</div>}
                {op.bebida          && <div><span className="text-[10px] text-gray-400 block">🥤 Bebida</span>{op.bebida}</div>}
                {op.postre          && <div><span className="text-[10px] text-gray-400 block">🍰 Postre</span>{op.postre}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
